import React, { useEffect, useState } from 'react'
import { Box, Flex } from 'rebass'
import {
  Button,
  BtnIcon,
  Label,
} from '../../Common/NeutronComponents/atoms/button/Button'
import { Search } from '../../Common/Search/Search'
import TileCard from './TileCard'
import CreateBanner from './CreateBanner'
import {
  getAllBanners,
  voteBanner,
  updateBannerStatus,
} from '../../../store/actions/banners'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from '../../../store/rootReducer'
import { AuthStateProps } from '../../../interfaces/auth'
import { BannerStatus, GETBannerDataProps } from '../../../interfaces/banners'
import { parseUtcDate } from '../../../utils/dateTime'

const normalizeBannerStatus = (status?: string): string =>
  (status || '').trim().toLowerCase().replace(/\s+/g, '')

const getBannerStatusPriority = (status?: string): number => {
  const normalizedStatus = normalizeBannerStatus(status)

  if (
    normalizedStatus === normalizeBannerStatus(BannerStatus.PendingApproval)
  ) {
    return 1
  }

  if (normalizedStatus === normalizeBannerStatus(BannerStatus.Upcoming)) {
    return 2
  }

  if (normalizedStatus === normalizeBannerStatus(BannerStatus.Active)) {
    return 3
  }

  return 4
}

export default function Banners() {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingBanner, setEditingBanner] = useState<GETBannerDataProps>()

  const dispatch = useDispatch()

  const { token } = useSelector<AppState, Partial<AuthStateProps>>(
    ({ auth: { authorizedOrganizations, options, token } }) => ({
      authorizedOrganizations,
      options,
      token,
    })
  )

  const refreshBanners = React.useCallback(async () => {
    await dispatch<any>(getAllBanners({ token: token || '' }))
  }, [dispatch, token])

  // Call API on page load
  useEffect(() => {
    refreshBanners()
  }, [refreshBanners])

  const banners = useSelector((state: AppState) => state.banner.banners)

  const handleVote = async (vote: boolean, bannerID: string) => {
    console.log('Vote handled')

    const result = await dispatch<any>(
      voteBanner({
        token: token || '',
        bannerId: bannerID,
        vote: vote,
      })
    )

    if (result) {
      console.log('Vote successful')
      await refreshBanners()
    } else {
      console.error('Vote failed')
    }
  }

  const handleDelete = async (bannerID: string) => {
    console.log('Deleting Banner')

    const result = await dispatch<any>(
      updateBannerStatus({
        token: token || '',
        bannerId: bannerID,
        status: BannerStatus.Cancelled,
      })
    )

    if (result) {
      console.log('Updated banner status')
      await refreshBanners()
    } else {
      console.log('Banner Update fail')
    }
  }

  const handleClose = async (bannerID: string) => {
    console.log('Closing Banner')

    const result = await dispatch<any>(
      updateBannerStatus({
        token: token || '',
        bannerId: bannerID,
        status: BannerStatus.Closed,
      })
    )

    if (result) {
      console.log('Updated banner status to Closed')
      await refreshBanners()
    } else {
      console.log('Banner Update fail')
    }
  }

  // Debug: print fetched banners
  console.log('Banners fetched:', banners)

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingBanner(undefined)
  }

  return (
    <Box p={4}>
      {/* Top Bar: Search + Create Button */}
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Flex justifyContent={'flex-start'} alignItems={'center'}>
          <Box sx={{ margin: '10px 20px 0 10px' }}>
            <Search
              onDebounce={(term: string): void => {
                setDebouncedSearchTerm(term)
              }}
            />
          </Box>

          <Button
            color={'primary'}
            onClick={() => {
              setEditingBanner(undefined)
              setShowModal(true)
            }}
          >
            <BtnIcon value="add_circle" position="left" />
            <Label value="Create New" />
          </Button>
        </Flex>
      </Flex>

      {/* Tile Grid */}
      <Flex flexWrap="wrap" mx={-2}>
        {banners
          .filter((item) => {
            if (!debouncedSearchTerm) return true
            const searchText = debouncedSearchTerm.toLowerCase()
            return (
              item.header?.toLowerCase().includes(searchText) ||
              item.contents?.toLowerCase().includes(searchText) ||
              item.createdBy?.toLowerCase().includes(searchText)
            )
          })
          .slice()
          .sort((a, b) => {
            const priorityA = getBannerStatusPriority(a.bannerStatus)
            const priorityB = getBannerStatusPriority(b.bannerStatus)

            // sort by status priority first
            const priorityDiff = priorityA - priorityB
            if (priorityDiff !== 0) return priorityDiff

            // tie-breaker: latest start date first
            return (
              (parseUtcDate(b.startDate)?.getTime() ?? 0) -
              (parseUtcDate(a.startDate)?.getTime() ?? 0)
            )
          })
          .map((item) => {
            console.log('Banner Item:', item)

            return (
              <Box key={item.id} width={[1, 1 / 2, 1 / 3]} px={2} mb={3}>
                <TileCard
                  id={item.id}
                  bannerStatus={item.bannerStatus}
                  bannerSeverity={item.bannerSeverity}
                  bannerType={item.bannerType}
                  orgName={item.header}
                  startTime={item.startDate}
                  endTime={item.endDate}
                  message={item.contents}
                  createdBy={item.createdBy}
                  createdBy34={item.createdBy34}
                  approvalList={item.approvalList}
                  scope={item.scope}
                  onVoteUp={() => handleVote(true, item.id)}
                  onVoteDown={() => handleVote(false, item.id)}
                  onDelete={() => handleDelete(item.id)}
                  onClose={() => handleClose(item.id)}
                  onEdit={() => {
                    setEditingBanner(item)
                    setShowModal(true)
                  }}
                />
              </Box>
            )
          })}
      </Flex>

      {/* Simple Popup */}
      {showModal && (
        <CreateBanner
          onClose={handleCloseModal}
          refreshBanners={refreshBanners}
          initialBanner={editingBanner}
        />
      )}
    </Box>
  )
}
